/* Home — handoff screen 01-Home.png. */
import { useWallet } from '../contexts/WalletContext.jsx';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Footer, TopBar } from '../app/AppShell.jsx';
import { useSession } from '../app/session.jsx';
import { Icon, ToolTile } from '../ui/index.jsx';
import { comingUp, greeting } from '../features/home/events.js';
import { handToPrint } from '../features/print/handoff.js';
import RecentDesigns from '../features/home/RecentDesigns.jsx';
import '../features/home/home.css';
import { useTranslation } from 'react-i18next';

function HeroArt() {
  const { t } = useTranslation('bv');
  return (
    <div className="bv-hero__art" aria-hidden="true">
      <span className="bv-hero__sun" />
      <span className="bv-hero__dash bv-hero__dash--purple" />
      <span className="bv-hero__dash bv-hero__dash--coral" />
      <span className="bv-hero__hill bv-hero__hill--green" />
      <span className="bv-hero__hill bv-hero__hill--purple" />
      <span className="bv-hero__hill bv-hero__hill--coral" />
      <div className="bv-hero__card bv-hero__card--blue"><span className="bv-hero__cardblob" /><b>{t('home.art.water')}</b><small>{t('home.art.waterSub')}</small></div>
      <div className="bv-hero__card bv-hero__card--purple"><b>{t('home.art.fractions')}</b><small>{t('home.art.fractionsSub')}</small></div>
      <div className="bv-hero__card bv-hero__card--yellow"><Icon name="sparkle" size={30} className="bv-hero__spark" /><b>{t('home.art.read')}</b><small>{t('home.art.readSub')}</small></div>
    </div>
  );
}

function DropTile() {
  const { t } = useTranslation('bv');
  const navigate = useNavigate();
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const go = (files) => {
    const ok = [...files].filter((f) => /^(image\/(png|jpeg|webp)|application\/pdf)$/.test(f.type) || /\.pdf$/i.test(f.name));
    if (!ok.length) return;
    handToPrint(ok);
    navigate('/print');
  };
  return (
    <ToolTile as="div" color="blue" icon="cursor" title={t('home.tiles.drop')} subtitle={t('home.tiles.dropSub')} className="bv-home__drop">
      <button type="button" className={`bv-home__dropzone${over ? ' is-over' : ''}`} onClick={() => input.current && input.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); go(e.dataTransfer.files); }} data-testid="home-drop">
        <Icon name="layers" size={34} />
        <span className="bv-home__droptitle">{t('home.dropHere')}</span>
        <span className="bv-home__dropsub">{t('home.dropBrowse')}</span>
        <input ref={input} type="file" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
          onChange={(e) => { go(e.target.files); e.target.value = ''; }} />
      </button>
    </ToolTile>
  );
}

export default function HomePage() {
  const { t, i18n } = useTranslation('bv');
  const { session } = useSession();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const { getCost } = useWallet(); // desktop AI card shows the credit cost
  const cost = getCost('ai_poster_generate');
  const name = (session && session.name) || '';
  const events = comingUp(new Date(), 4, t);
  const generate = (e) => {
    e.preventDefault();
    navigate(`/design/ai${prompt.trim() ? `?prompt=${encodeURIComponent(prompt.trim())}` : ''}`);
  };
  return (
    <>
      <div className="bv-content bv-home">
        <TopBar logo />
        <section className="bv-hero" aria-labelledby="bv-hero-title">
          <div className="bv-hero__text">
            <p className="bv-hero__hello">{t(greeting())}{name ? `, ${name}` : ''}</p>
            <h1 id="bv-hero-title" className="bv-hero__title">{t('home.title')}</h1>
            <form className="bv-hero__prompt" onSubmit={generate} role="search" data-tour="hero-generate">
              <Icon name="sparkle" size={22} className="bv-hero__promptspark" />
              <input value={prompt} onChange={(e) => setPrompt(e.target.value)} aria-label={t('home.promptAria')}
                placeholder={t('home.promptPlaceholder')} maxLength={500} data-testid="home-prompt" />
              <button type="submit" className="bv-btn bv-btn--coral bv-hero__go" data-testid="home-generate">{t('home.generate')}{cost ? ` · ${t('common.credits', { count: cost })}` : ''} <Icon name="arrow" size={20} /></button>
            </form>
            <div className="bv-hero__coming">
              <span className="bv-hero__cominglabel">{t('home.comingUp')}</span>
              {events.map((ev) => (
                <button key={ev.name} type="button" className="bv-hero__event" onClick={() => setPrompt(t('home.posterFor', { event: ev.name }))}
                  title={ev.date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}>
                  <span className={`bv-hero__dot bv-dot--${ev.dot}`} />{ev.name}
                </button>
              ))}
            </div>
          </div>
          <HeroArt />
        </section>

        <div className="bv-home__grid">
          <DropTile />
          <ToolTile as={Link} to="/scan" color="coral" icon="camera" title={t('home.tiles.scan')} subtitle={t('home.tiles.scanSub')} className="bv-home__t2" />
          <ToolTile as={Link} to="/design" color="yellow" icon="design" title={t('home.tiles.design')} subtitle={t('home.tiles.designSub')} className="bv-home__t3" />
          <ToolTile as={Link} to="/browser" color="green" icon="canva" title={t('home.tiles.canva')} subtitle={t('home.tiles.canvaSub')} className="bv-home__t4" />
          <ToolTile as={Link} to="/library" color="purple" icon="library" title={t('home.tiles.library')} subtitle={t('home.tiles.librarySub')} className="bv-home__t5" />
          <RecentDesigns className="bv-home__recent" />
        </div>
      </div>
      <Footer />
    </>
  );
}
